// /notary/documents — Wave 59: hub of the six Notary document families (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/notary/documents`;
const TITLE = "เอกสารที่ทนาย Notary Public รับรองได้ — ครบ 6 กลุ่มเอกสาร | NYC";
const DESC =
  "รวมเอกสารที่ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองได้ 6 กลุ่ม พร้อมเช็กลิสต์เตรียมเอกสาร ขั้นตอนใช้ต่างประเทศ และข้อจำกัดที่ควรรู้ก่อนยื่น";

export const Route = createFileRoute("/notary/documents/")({
  loader: async () => {
    const { NOTARY_DOC_GROUPS } = await import("@/data/notary-doc-groups");
    return {
      __d: { NOTARY_DOC_GROUPS },
      groups: NOTARY_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, "/notary/documents", "th") },
      { name: "description", content: ctaDesc(DESC, "/notary/documents", "th") },
      { property: "og:title", content: ctaTitle(TITLE, "/notary/documents", "th") },
      { property: "og:description", content: ctaDesc(DESC, "/notary/documents", "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/notary/documents` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารที่ทนาย Notary Public รับรอง",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}#${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: NotaryDocumentsPage,
});

function NotaryDocumentsPage() {
  const { __d: { NOTARY_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/notary-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/notary" className="hover:underline">
          Notary Public
        </Link>{" "}
        / เอกสารที่รับรองได้
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        เอกสารที่ทนาย Notary Public รับรองได้ — ครบ 6 กลุ่ม
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ที่ขึ้นทะเบียนกับสภาทนายความ
        รับรองได้ 3 ลักษณะหลัก คือ รับรองสำเนาถูกต้อง รับรองลายมือชื่อที่ลงต่อหน้า และรับคำสาบานในคำให้การเป็นหนังสือ
        หน้านี้รวบรวมเอกสารทั้งหมดตามกลุ่มการใช้งาน พร้อมเช็กลิสต์และข้อจำกัดที่ควรรู้ก่อนยื่น
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>สถานะ Apostille ของไทย:</strong> ไทยยื่นภาคยานุวัติสารเมื่อ {APOSTILLE_STATUS.accessionDepositTh}{" "}
        และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ {APOSTILLE_STATUS.entryIntoForceTh} ก่อนถึงวันดังกล่าว
        เอกสารยังต้องผ่านกรมการกงสุลและสถานทูตประเทศปลายทางตามเดิม{" "}
        <a
          href={APOSTILLE_STATUS.sourceUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
        >
          ที่มา: HCCH
        </a>
      </aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {NOTARY_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/notary/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>


      {NOTARY_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>

          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <h3 className="mt-6 text-lg font-semibold">เอกสารในกลุ่มนี้</h3>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            {g.documentsTh.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">รูปแบบการรับรองที่ใช้</h3>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            {g.actsTh.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">เช็กลิสต์ก่อนมาดำเนินการ</h3>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            {g.checklistTh.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>

          <h3 className="mt-6 text-lg font-semibold">คำถามที่พบบ่อย</h3>
          <div className="mt-2 space-y-3">
            {g.faqsTh.map((f) => (
              <div key={f.q}>
                <p className="font-medium voice-faq-q">{f.q}</p>
                <p className="text-muted-foreground voice-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      ))}
          <ServiceAuthorityBlock topic="notary" locale="th" />
    </main>
  );
}
